import "./table-settings-meta";
import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { TableSettingsDto, TableSortOptionDto } from "@/client";
import { sanitizeSortBy } from "@/lib/router";

/**
 * The pure half of per-user table layouts (ID-6897): what the table declares, what the profile
 * stores, and how the two meet. Nothing here touches React or the network.
 *
 * Contract with the backend (and with layouts saved from other devices or roles):
 * - `columnOrder` lists every column the layout knows about. A column absent from it is new to the
 *   layout and takes its declared default; one that is present follows `visibleColumns`.
 * - `visibleColumns` is never written empty -- the server reads an empty list as "unset".
 * - Ids the current device or role does not declare (a column removed on mobile, one gated by a
 *   permission) are passed through untouched, so a change made on a phone cannot wipe the
 *   desktop-only columns from the saved layout.
 */
export type ColumnSpec = {
  id: string;
  canHide: boolean;
  defaultHidden: boolean;
};

export type SavedLayout = Pick<
  TableSettingsDto,
  "visibleColumns" | "columnOrder"
>;

/** What one write to the profile would carry; compared to the last successful write to find changes. */
export type TableSettingsSnapshot = {
  visibleColumns: string[];
  columnOrder: string[];
  sort: TableSortOptionDto[];
  pageSize?: number;
};

// Mirrors TanStack's own id derivation (explicit id, then the accessorKey with dots replaced, then a
// string header), so the ids written to the profile are the ones the table uses at runtime.
export const getColumnDefId = (
  column: ColumnDef<any, any>,
): string | undefined => {
  const { accessorKey } = column as { accessorKey?: unknown };
  return (
    column.id ??
    (typeof accessorKey === "string"
      ? accessorKey.replaceAll(".", "_")
      : undefined) ??
    (typeof column.header === "string" ? column.header : undefined)
  );
};

// Group columns (`columns: [...]`) are containers: the manager, like TanStack, deals in leaves.
const leafColumns = (columns: ColumnDef<any, any>[]): ColumnDef<any, any>[] =>
  columns.flatMap((column) =>
    "columns" in column && Array.isArray(column.columns)
      ? leafColumns(column.columns)
      : [column],
  );

export const specsOf = (columns: ColumnDef<any, any>[]): ColumnSpec[] => {
  const specs: ColumnSpec[] = [];
  const seen = new Set<string>();
  for (const column of leafColumns(columns)) {
    const id = getColumnDefId(column);
    if (id === undefined || seen.has(id)) {
      continue;
    }
    seen.add(id);
    specs.push({
      id,
      canHide: column.enableHiding !== false,
      defaultHidden: column.meta?.defaultHidden === true,
    });
  }
  return specs;
};

export const hasVisibleHideable = (
  specs: ColumnSpec[],
  visibility: VisibilityState,
) => specs.some((spec) => spec.canHide && visibility[spec.id] !== false);

/** The visibility to render for these columns given a saved layout (or none): one explicit boolean per id. */
export const reconcileVisibility = (
  specs: ColumnSpec[],
  saved?: SavedLayout,
): VisibilityState => {
  // Without a saved order every declared column counts as known, so a saved `visibleColumns` alone
  // still applies instead of being read as "all of these are new".
  const known = new Set(
    saved?.columnOrder?.length ? saved.columnOrder : specs.map((s) => s.id),
  );
  const visibleSaved = saved?.visibleColumns ?? [];
  const useSaved = visibleSaved.length > 0;
  const visible = new Set(visibleSaved);

  const visibility: VisibilityState = {};
  for (const spec of specs) {
    visibility[spec.id] = !spec.canHide
      ? true
      : useSaved && known.has(spec.id)
        ? visible.has(spec.id)
        : !spec.defaultHidden;
  }
  // The UI never lets the last column go, but a layout saved for another column set might leave
  // this one empty: show the first column that is visible by default rather than a blank table.
  if (!hasVisibleHideable(specs, visibility)) {
    const fallback =
      specs.find((spec) => spec.canHide && !spec.defaultHidden) ??
      specs.find((spec) => spec.canHide);
    if (fallback) {
      visibility[fallback.id] = true;
    }
  }
  return visibility;
};

const unique = (ids: string[]) => [...new Set(ids)];

/** The lists to store for this visibility, keeping whatever the saved layout knows that this column set does not. */
export const toTableSettingsPatch = (
  specs: ColumnSpec[],
  visibility: VisibilityState,
  saved?: SavedLayout,
): Pick<TableSettingsSnapshot, "visibleColumns" | "columnOrder"> => {
  const current = specs.map((spec) => spec.id);
  const currentSet = new Set(current);
  const passThrough = (ids?: string[]) =>
    (ids ?? []).filter((id) => !currentSet.has(id));
  return {
    columnOrder: unique([...current, ...passThrough(saved?.columnOrder)]),
    visibleColumns: unique([
      ...current.filter((id) => visibility[id] !== false),
      ...passThrough(saved?.visibleColumns),
    ]),
  };
};

const SORT_DIRECTIONS = new Set(["asc", "desc"]);

const asSortOption = (value: unknown): TableSortOptionDto | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    if (trimmed.startsWith("{")) {
      try {
        return asSortOption(JSON.parse(trimmed));
      } catch {
        return undefined;
      }
    }
    return { field: trimmed, dir: "asc" };
  }
  if (typeof value === "object" && value !== null && "field" in value) {
    const { field, dir } = value as { field?: unknown; dir?: unknown };
    if (typeof field !== "string" || !field) {
      return undefined;
    }
    if (dir === undefined) {
      return { field, dir: "asc" };
    }
    return typeof dir === "string" && SORT_DIRECTIONS.has(dir)
      ? { field, dir: dir as TableSortOptionDto["dir"] }
      : undefined;
  }
  return undefined;
};

/** The table's URL sort (JSON strings, bare field names, or objects) as the profile stores it. */
export const parseSortValues = (value: unknown): TableSortOptionDto[] => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];
  return values
    .map(asSortOption)
    .filter((option): option is TableSortOptionDto => option !== undefined);
};

export type TableSettingsSearchOptions = {
  /** The sort fields the table's API accepts; without it no sort is seeded. */
  sortEnum?: readonly string[];
  /** The page sizes the table offers; a stored size outside them is ignored. */
  pageOptions?: readonly number[];
  /** The URL keys the table reads its sort and page size from: `sortBy` and `limit` unless namespaced. */
  keys?: { sort?: string; limit?: string };
};

/** The URL search a saved layout seeds on route entry: the sort as the table's JSON sort entries, the page size as its limit. */
export const tableSettingsToSearch = (
  value: TableSettingsDto | undefined,
  { sortEnum, pageOptions, keys }: TableSettingsSearchOptions,
): Record<string, unknown> => {
  const seed: Record<string, unknown> = {};
  if (sortEnum && value?.sort?.length) {
    const sortBy = sanitizeSortBy(
      value.sort.map((s) => JSON.stringify({ field: s.field, dir: s.dir })),
      sortEnum,
    );
    if (sortBy?.length) {
      seed[keys?.sort ?? "sortBy"] = sortBy;
    }
  }
  const pageSize = value?.pageSize;
  if (
    typeof pageSize === "number" &&
    Number.isInteger(pageSize) &&
    pageSize > 0 &&
    (!pageOptions || pageOptions.includes(pageSize))
  ) {
    seed[keys?.limit ?? "limit"] = pageSize;
  }
  return seed;
};
